import React, { createContext, useContext, useCallback, useMemo } from "react";
import { useAuth } from "../../../shared/src/auth";
import {
  ChatMessage,
  Conversation,
  ConversationRequest,
  FrontEndSettings,
} from "./models";
import {
  callConversationApi as callConversationApiBase,
  submitFeedback as submitFeedbackBase,
  historyRead as historyReadBase,
  historyList as historyListBase,
  historyUpdate as historyUpdateBase,
  historyRename as historyRenameBase,
  historyDelete as historyDeleteBase,
  historyDeleteAll as historyDeleteAllBase,
  getFrontEndSettings as getFrontEndSettingsBase,
  FeedbackRequest,
} from "./api";

interface AuthenticatedApiContextType {
  getToken: () => Promise<string>;
  callConversationApi: (options: ConversationRequest, abortSignal: AbortSignal) => Promise<Response>;
  submitFeedback: (feedback: FeedbackRequest) => Promise<boolean>;
  historyRead: (convId: string) => Promise<ChatMessage[]>;
  historyList: (offset?: number) => Promise<Conversation[] | null>;
  historyUpdate: (messages: ChatMessage[], convId: string) => Promise<Response>;
  historyRename: (convId: string, title: string) => Promise<Response>;
  historyDelete: (convId: string) => Promise<Response>;
  historyDeleteAll: () => Promise<Response>;
  getFrontEndSettings: () => Promise<FrontEndSettings>;
}

const AuthenticatedApiContext = createContext<AuthenticatedApiContextType | null>(null);

interface AuthenticatedApiProviderProps {
  children: React.ReactNode;
}

export const AuthenticatedApiProvider: React.FC<AuthenticatedApiProviderProps> = ({ children }) => {
  const { getAccessToken, login } = useAuth();

  const getToken = useCallback(async (): Promise<string> => {
    try {
      return await getAccessToken();
    } catch (error) {
      console.error("Failed to acquire access token:", error);
      login();
      throw new Error("Authentication required");
    }
  }, [getAccessToken, login]);


  const callConversationApi = useCallback(
    async (options: ConversationRequest, abortSignal: AbortSignal): Promise<Response> => {
      const token = await getToken();
      return callConversationApiBase(options, abortSignal, token);
    },
    [getToken]
  );

  const submitFeedback = useCallback(
    async (feedback: FeedbackRequest): Promise<boolean> => {
      const token = await getToken();
      return submitFeedbackBase(feedback, token);
    },
    [getToken]
  );

  const historyRead = useCallback(
    async (convId: string): Promise<ChatMessage[]> => {
      const token = await getToken();
      return historyReadBase(convId, token);
    },
    [getToken]
  );

  const historyList = useCallback(
    async (offset = 0): Promise<Conversation[] | null> => {
      const token = await getToken();
      return historyListBase(offset, token);
    },
    [getToken]
  );

  const historyUpdate = useCallback(
    async (messages: ChatMessage[], convId: string): Promise<Response> => {
      const token = await getToken();
      return historyUpdateBase(messages, convId, token);
    },
    [getToken]
  );

  const historyRename = useCallback(
    async (convId: string, title: string): Promise<Response> => {
      const token = await getToken();
      return historyRenameBase(convId, title, token);
    },
    [getToken]
  );

  const historyDelete = useCallback(
    async (convId: string): Promise<Response> => {
      const token = await getToken();
      return historyDeleteBase(convId, token);
    },
    [getToken]
  );

  const historyDeleteAll = useCallback(async (): Promise<Response> => {
    const token = await getToken();
    return historyDeleteAllBase(token);
  }, [getToken]);

  const getFrontEndSettings = useCallback(async (): Promise<FrontEndSettings> => {
    const token = await getToken();
    return getFrontEndSettingsBase(token);
  }, [getToken]);

  const value = useMemo(
    () => ({
      getToken,
      callConversationApi,
      submitFeedback,
      historyRead,
      historyList,
      historyUpdate,
      historyRename,
      historyDelete,
      historyDeleteAll,
      getFrontEndSettings,
    }),
    [
      getToken,
      callConversationApi,
      submitFeedback,
      historyRead,
      historyList,
      historyUpdate,
      historyRename,
      historyDelete,
      historyDeleteAll,
      getFrontEndSettings,
    ]
  );

  return (
    <AuthenticatedApiContext.Provider value={value}>
      {children}
    </AuthenticatedApiContext.Provider>
  );
};

export const useAuthenticatedApi = (): AuthenticatedApiContextType => {
  const context = useContext(AuthenticatedApiContext);
  if (!context) {
    throw new Error("useAuthenticatedApi must be used within an AuthenticatedApiProvider");
  }
  return context;
};

export default AuthenticatedApiContext;
